import FeaturesMap from '@apps-common/ui-maps/FeaturesMap.jsx';
import GeojsonLayer from '@apps-common/ui-maps/GeojsonLayer.jsx';
import {Alert, Box, Button, DialogContent, Stack} from '@mui/material';
import {pink} from '@mui/material/colors';
import {union} from '@turf/union';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import ActionsBar from './ActionsBar.jsx';
import AlternateCard from './AlternateCard.jsx';
import {importFeatures} from './util.js';
import {EVENT_ACTIONS} from '@src/analytics.js';
import {
  useGetAlternates,
  useValidateFeature,
} from '@src/api/featureCollections.js';
import LoadingMessage from '@src/components/common/LoadingMessage.tsx';
import {useTrackSectionEvent} from '@src/hooks/useTrackSectionEvent.js';

const MAX_PAYLOAD_BYTES = 5000000; // 5MB

const ValidationStep = ({group, onImport, onClose}) => {
  const trackSectionEvent = useTrackSectionEvent();

  const [isImporting, setIsImporting] = useState(false);
  const [features, setFeatures] = useState();
  const [importedFeature, setImportedFeature] = useState();
  const [importErrors, setImportErrors] = useState([]);
  const [importWarnings, setImportWarnings] = useState([]);

  const bytes = importedFeature
    ? Buffer.byteLength(JSON.stringify(importedFeature))
    : null;

  const files = useMemo(() => group?.files, [group?.files]);

  useEffect(() => {
    if (!files || files.length === 0) {
      return;
    }

    const importFiles = async () => {
      setIsImporting(true);

      try {
        const {
          features = [],
          errors = [],
          warnings = [],
        } = await importFeatures(files);
        const singleFeature =
          features.length > 1
            ? union({type: 'FeatureCollection', features})
            : features[0];

        setFeatures(features);
        setImportedFeature(singleFeature);
        setImportErrors(errors);
        setImportWarnings(warnings);
      } catch (error) {
        setImportErrors([error.message]);
      } finally {
        setIsImporting(false);
      }
    };

    importFiles();
  }, [files]);

  const {
    error: validationError,
    isSuccess: validationSuccess,
    isLoading: isValidating,
  } = useValidateFeature(importedFeature, {
    enabled: !!importedFeature && bytes <= MAX_PAYLOAD_BYTES,
  });

  const {data: alternate, isLoading: loadingAlternate} = useGetAlternates(
    importedFeature,
    {
      enabled:
        !!importedFeature && validationError?.status === 400 && !isValidating,
      select: featureCollection => featureCollection.features[0],
    },
  );

  useEffect(() => {
    if (validationSuccess) {
      onImport(importedFeature);
      trackSectionEvent(EVENT_ACTIONS.IMPORT, 'valid feature');
    }
  }, [importedFeature, onImport, trackSectionEvent, validationSuccess]);

  const validationErrorMessages =
    validationError?.response?.data?.at(0)?.geometry ?? [];

  const loadingMessage = isImporting
    ? 'Importing...'
    : isValidating || loadingAlternate
      ? 'Validating...'
      : '';

  return (
    <>
      <DialogContent dividers sx={{display: 'flex'}}>
        {!!loadingMessage && <LoadingMessage title={loadingMessage} />}

        {importErrors.length > 0 && (
          <Stack sx={{gap: 1, width: '100%'}}>
            {importErrors.map(error => (
              <Alert key={error} severity="error">
                {error}
              </Alert>
            ))}
          </Stack>
        )}

        {importWarnings.length > 0 && (
          <Stack sx={{gap: 1, width: '100%'}}>
            {importWarnings.map(warning => (
              <Alert key={warning} severity="error">
                {warning}
              </Alert>
            ))}
          </Stack>
        )}

        {!loadingMessage && importErrors.length === 0 && !!alternate && (
          <Stack
            direction="row"
            sx={{
              width: '100%',
              flexShrink: 0,
              flexGrow: 0,
              height: '100%',
              gap: 2,
            }}
          >
            <Box sx={{width: '50%'}}>
              <FeaturesMap features={features}>
                <GeojsonLayer
                  features={[alternate]}
                  style={[
                    {
                      'stroke-width': 5,
                      'stroke-color': `${pink[300]}66`,
                    },
                    {
                      'stroke-width': 3,
                      'stroke-color': `${pink[500]}`,
                    },
                  ]}
                />
              </FeaturesMap>
            </Box>

            <Box sx={{width: '50%'}}>
              <AlternateCard
                originalFeature={importedFeature}
                alternateFeature={alternate}
                validationErrorMessages={validationErrorMessages}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <ActionsBar onClose={onClose}>
        <Button
          variant="contained"
          disabled={!!loadingMessage}
          onClick={() => {
            trackSectionEvent(EVENT_ACTIONS.IMPORT, 'alternate feature');
            onImport(alternate);
          }}
        >
          Import
        </Button>
      </ActionsBar>
    </>
  );
};

ValidationStep.propTypes = {
  group: PropTypes.object,
  onImport: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default ValidationStep;
